import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import moment from 'moment';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { CustomTextConfigurationApi } from 'Api/CustomTextConfiguration/CustomTextConfigurationApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { FormFieldSelectOrCreate } from 'Components/FormField/FormFieldSelectOrCreate/FormFieldSelectOrCreate';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { InherentRiskQuestionnaireForm, InherentRiskQuestionnaireUserTypeAndData, getDataIsInsufficientForSubmittingIrqReason } from 'Components/UcrmTprm/InherentRiskQuestionnaireForm/InherentRiskQuestionnaireForm';
import { IrqTargetCompletionDateSelection, getDefaultIrqTargetCompletionDate, getIrqTargetCompletionDateSelectionProps } from 'Components/UcrmTprm/IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';
import { SubmitIrqSelections, SubmitIrqSelectionsValue } from 'Components/UcrmTprm/SubmitIrqSelections/SubmitIrqSelections';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_SUBMIT } from 'Config/Icons';
import { UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { ACTION_CREATED, ACTION_SAVED, ACTION_SKIPPED, ACTION_SUBMITTED, ASSESSMENT_SUBJECT_FORM_TEST_ID, IRQ_FORM_TEST_ID, ORGANIZATION_FORM_TEST_ID, SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS, SEARCH_PARAM_ASSESSMENT_SUBJECT_ID, SEARCH_PARAM_COMPLETING_INTAKE, SEARCH_PARAM_IRQ_ACTIONS, SEARCH_PARAM_ORGANIZATION_ACTIONS, SEARCH_PARAM_ORGANIZATION_ID, SEARCH_PARAM_WORKFLOW_STEP, SUBMIT_FORM_TEST_ID, SUCCESS_MESSAGE_IRQ, UCRM_SUCCESS_MESSAGE_BUSINESS, UCRM_SUCCESS_MESSAGE_USE_CASE } from 'Config/Text';
import { UCRM_BUSINESS_NAME_SELECT_OR_CREATE, UCRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITH_RECOMMENDATION, UCRM_INTAKE_BUSINESS_ALREADY_FINALIZED, UCRM_INTAKE_BUSINESS_ALREADY_SUBMITTED_TO_BE_FINALIZED, UCRM_INTAKE_BUSINESS_CANNOT_BE_SUBMITTED_BECAUSE_NON_UCRM_USER_IS_NOT_CREATOR, UCRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED, UCRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_USE_CASE_STEP_SKIPPED, UCRM_INTAKE_SUBMIT_BUSINESS_CHECKBOX_NON_UCRM_USER, UCRM_INTAKE_SUBMIT_BUSINESS_CHECKBOX_UCRM_USER, UCRM_INTAKE_SUBMIT_IRQ_CHECKBOX_UCRM_USER, UCRM_INTAKE_SUBMIT_USE_CASE_CHECKBOX_NON_UCRM_USER, UCRM_INTAKE_SUBMIT_USE_CASE_CHECKBOX_UCRM_USER, UCRM_INTAKE_USE_CASE_ALREADY_FINALIZED, UCRM_INTAKE_USE_CASE_ALREADY_SUBMITTED_TO_BE_FINALIZED, UCRM_INTAKE_USE_CASE_CANNOT_BE_SUBMITTED_BECAUSE_USE_CASE_NOT_CREATED } from 'Config/Tooltips';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { buildUrlForIntakeUrlState, getRecommendedDueDiligenceWorkflowDueDate } from 'Helpers/UcrmTprm';
import { TPRM_ROLES, UCRM_ROLES, UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AssessmentSubjectType, OrganizationSelectionOrCreationState, OrganizationType } from 'Models/AssessmentSubject';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceTimeframes, IntakeUrlState, IntakeWorkflowStep } from 'Models/DueDiligenceWorkflow';
import { InherentRiskQuestionnaireResponse } from 'Models/InherentRisk';
import { FinalizationState, userCanSubmitOrFinalizeDraft, userHasFullAccessOrCreatedDraft } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { ThirdPartyService } from 'Models/TPRM';
import { OptionType } from 'Models/Types/GlobalType';
import { BusinessForNonUcrmUser, BusinessResponse, BusinessResponseWithUseCases, BusinessWithUseCasesForNonUcrmUser, CompleteUcrmIntakeRequest, DEFAULT_UCRM_INTAKE_INSTRUCTIONS, UcrmEntities, UcrmSubmitInherentRiskQuestionnaireSelections, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';

import styles from './UcrmIntake.module.css';
import { ManageBusinessFormFields } from '../ManageBusiness/ManageBusinessFormFields/ManageBusinessFormFields';
import { ManageUseCaseFormFields } from '../ManageUseCase/ManageUseCaseFormFields/ManageUseCaseFormFields';

export interface UcrmIntakeProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    authHandler: AuthHandler;
    customTextConfigurationApi: CustomTextConfigurationApi;
    documentApi: DocumentApi;
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    issuesApi: IssuesApi;
    exceptionsApi: ExceptionsApi;
    ucrmApi: ClientUcrmApi;
    /** The API for the TPRM module, used to get the TPRM third-party services that the use case can be associated with. Not to be confused with `ucrmApi`. */
    tprmModuleApi: ClientTPRMApi;
}

/**
 * Renders a four-step process for the intake of a new business and/or use case:
 * * Create/edit a new business or edit an existing business
 * * Create/edit a new use case or edit an existing use case
 * * Edit the use case's IRQ
 * * Submit drafted/saved items, if desired
 *
 * Can be used by a user with a UCRM role or a user without one, the main difference in experience being that users without a UCRM role cannot submit the IRQ. When a UCRM user submits a business or use case, it is finalized; for other users, it is submitted to be reviewed by the business's or use case's manager.
 * Keeps state in sync with URL search params, such that progress can be resumed seamlessly if the page is reloaded. This is a common occurrence, since there is much to complete on this page, and the app automatically logs out after 15 minutes of inactivity.
 */
export const UcrmIntake = (props: UcrmIntakeProps) => {
    // Navigation utilities, and URL search params that stay in sync with the page's state.
    const location = useLocation();
    const navigate = useNavigate();

    // Users, which are used to display manager names if a user without a UCRM role submits the business and/or use case to be finalized.
    const { users } = useCachedData();

    // Workflow state that is initially obtained from and then forever synchronized with the params in the URL. Facilitates intake workflow progress being maintained even if the page is refreshed, or if the user navigates here from the Businesses and Use Cases listing.
    const [urlState, setUrlState] = useState<IntakeUrlState>(() => {
        const searchParams = new URLSearchParams(location.search);

        const completingIntake = searchParams.get(SEARCH_PARAM_COMPLETING_INTAKE) === 'true';
        const step = parseInt(searchParams.get(SEARCH_PARAM_WORKFLOW_STEP) ?? '0') as IntakeWorkflowStep;
        const organizationId = searchParams.get(SEARCH_PARAM_ORGANIZATION_ID) ?? undefined;
        const assessmentSubjectId = searchParams.get(SEARCH_PARAM_ASSESSMENT_SUBJECT_ID) ?? undefined;

        const organizationActions = (searchParams.get(SEARCH_PARAM_ORGANIZATION_ACTIONS) ?? '').split('-');
        const assessmentSubjectActions = (searchParams.get(SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS) ?? '').split('-');
        const irqActions = (searchParams.get(SEARCH_PARAM_IRQ_ACTIONS) ?? '').split('-');

        const _urlState: IntakeUrlState = {
            completingIntake,
            step,
            organizationId,
            assessmentSubjectId,
            organizationActions: {
                created: organizationActions.includes(ACTION_CREATED),
                saved: organizationActions.includes(ACTION_SAVED),
                submitted: organizationActions.includes(ACTION_SUBMITTED),
            },
            assessmentSubjectActions: {
                skipped: assessmentSubjectActions.includes(ACTION_SKIPPED),
                created: assessmentSubjectActions.includes(ACTION_CREATED),
                saved: assessmentSubjectActions.includes(ACTION_SAVED),
                submitted: assessmentSubjectActions.includes(ACTION_SUBMITTED),
            },
            irqActions: {
                skipped: irqActions.includes(ACTION_SKIPPED),
                saved: irqActions.includes(ACTION_SAVED),
                submitted: irqActions.includes(ACTION_SUBMITTED),
            },
        };

        return _urlState;
    });

    // Memoized information about the current user.
    const userHasFullUcrmAccess = useMemo(() => userHasAuthorizedRole(props.authHandler.getUserRoles(), UCRM_ROLES), [props.authHandler]);
    /** Mapping a use case to TPRM third-party services requires reading TPRM services, which requires a TPRM role in addition to a UCRM role. */
    const userCanMapTprmServices = useMemo(() => userHasFullUcrmAccess && userHasAuthorizedRole(props.authHandler.getUserRoles(), TPRM_ROLES), [props.authHandler, userHasFullUcrmAccess]);
    const userId = useMemo(() => props.authHandler.getAuthenticatedUserSubject()!, [props.authHandler]);

    // Data loaded initially to be used on the page.
    const [intakeInstructions, setIntakeInstructions] = useState<string>();
    const [businesses, setBusinesses] = useState<BusinessResponseWithUseCases[] | BusinessWithUseCasesForNonUcrmUser[]>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    // The "main data" of the page: the entities/selections that are made as the forms are completed.
    const [businessSelectionOrCreationState, setBusinessSelectionOrCreationState] = useState<OrganizationSelectionOrCreationState<UcrmEntities>>();
    const [useCase, setUseCase] = useState<AssessmentSubjectType<UcrmEntities> | null>();
    const [assessmentConfigurations, setAssessmentsConfiguration] = useState<DueDiligenceAssessmentConfiguration[] | null>();
    /** `null` if TPRM services can't be associated with the use case (i.e., the user lacks access to TPRM, or the request for them failed), in which case the use case form omits the associations. */
    const [tprmServices, setTprmServices] = useState<ThirdPartyService[] | null>();
    const [irqAndValidation, setIrqAndValidation] = useState<{ irq?: InherentRiskQuestionnaireResponse; submitIrqValidationErrorMessage?: string; highlightMissingInformation?: boolean }>();
    const [timeframes, setTimeframes] = useState<DueDiligenceTimeframes | null>();

    // Toasts and dialogs.
    const [toastContent, setToastContent] = useState<{ variant: 'success' | 'failure'; text: string }>();
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState<boolean>(false);

    // States that are used to determine which checkboxes are checked/enabled in the final step of the page.
    const [businessCheckboxChecked, setBusinessCheckboxChecked] = useState(true);
    const [useCaseCheckboxChecked, setUseCaseCheckboxChecked] = useState(true);
    const [irqCheckboxChecked, setIrqCheckboxChecked] = useState(userHasFullUcrmAccess);

    // The selections made in the Submit step of the page.
    const [submitIrqSelections, setSubmitIrqSelections] = useState<UcrmSubmitInherentRiskQuestionnaireSelections>({ assessment_selections: [] });
    // `SubmitIrqSelections` is shared with TPRM, so its selections include `third_party_contacts`, which does not exist for UCRM; drop it. `useCallback` is required because `SubmitIrqSelections` notifies its parent whenever this callback's identity changes.
    const onSubmitIrqSelectionsChanged = useCallback((selections: SubmitIrqSelectionsValue) => setSubmitIrqSelections({ assessment_selections: selections.assessment_selections }), []);
    // The user's selections for the IRQ target completion date and the due diligence workflow due date. When an override is undefined, the recommended date is used (and submitted) instead, so leaving a date picker untouched still sends the recommended default. The wrapper object distinguishes "the user has not made a selection" from "the user explicitly cleared the date".
    const [irqTargetCompletionDateOverride, setIrqTargetCompletionDateOverride] = useState<{ date: Date | undefined }>();
    const [dueDiligenceWorkflowDueDateOverride, setDueDiligenceWorkflowDueDateOverride] = useState<{ date: Date | undefined }>();

    /**
     * Keeps the validation message and checkbox enabled state in sync with the IRQ data.
     * This function _must_ be called whenever a new IRQ object is available.
     *
     * `checkIrqCheckboxIfAble` is used only when the IRQ is saved by the user, with the intention of saving them a click if they've completely filled out the IRQ before moving on to the Submit step.
     */
    const updateIrqAndSubmitIrqValidation = (irq?: InherentRiskQuestionnaireResponse, checkIrqCheckboxIfAble?: boolean) => {
        const submitIrqValidationErrorMessage = irq ? getDataIsInsufficientForSubmittingIrqReason(irq) : undefined;
        setIrqAndValidation((current) => ({ ...current, irq, submitIrqValidationErrorMessage }));

        if (submitIrqValidationErrorMessage) {
            setIrqCheckboxChecked(false);
        } else if (checkIrqCheckboxIfAble) {
            setIrqCheckboxChecked(true);
        }
    };

    /* Loads custom UCRM intake instructions (if they have been defined in Settings) when the component mounts. */
    useEffect(() => {
        const getCustomInstructions = async () => {
            try {
                const customTextConfiguration = (await props.customTextConfigurationApi.getCustomizedText()).data;
                setIntakeInstructions(customTextConfiguration.ucrm_intake_instructions ?? DEFAULT_UCRM_INTAKE_INSTRUCTIONS);
            } catch (error) {
                console.log(error.message);
                setLoadingErrorOccurred(true);
            }
        };
        getCustomInstructions();
    }, [props.customTextConfigurationApi]);

    /* Whenever relevant state changes, updates the URL search params that maintain progress on the page even through page reloads. */
    useEffect(() => {
        navigate(buildUrlForIntakeUrlState(ProductModule.UCRM, urlState), { replace: true });
    }, [navigate, urlState]);

    useEffect(() => {
        // Prevent re-fetching initial data.
        if (useCase !== undefined) {
            return;
        }

        const getUseCaseDetails = async (businessId: string, useCaseId: string): Promise<void> => {
            try {
                if (userHasFullUcrmAccess) {
                    const response = await props.ucrmApi.getUseCaseDetails(businessId, useCaseId);
                    setUseCase(response.data);
                } else {
                    const response = await props.ucrmApi.getUseCaseDetailsForNonUcrmUser(businessId, useCaseId);
                    setUseCase(response.data);
                }
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (urlState.organizationId && urlState.assessmentSubjectId) {
            getUseCaseDetails(urlState.organizationId, urlState.assessmentSubjectId);
        } else {
            setUseCase(null);
        }
    }, [urlState.organizationId, urlState.assessmentSubjectId, props.ucrmApi, userHasFullUcrmAccess, useCase]);

    useEffect(() => {
        // Prevent re-fetching initial data.
        if (irqAndValidation !== undefined) {
            return;
        }

        const getIrq = async (businessId: string, useCaseId: string): Promise<void> => {
            try {
                const response = await props.ucrmApi.getInherentRiskQuestionnaire(businessId, useCaseId);
                updateIrqAndSubmitIrqValidation(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (urlState.organizationId && urlState.assessmentSubjectId) {
            getIrq(urlState.organizationId, urlState.assessmentSubjectId);
        } else {
            updateIrqAndSubmitIrqValidation(undefined);
        }
    }, [urlState.organizationId, urlState.assessmentSubjectId, props.ucrmApi, userHasFullUcrmAccess, irqAndValidation]);

    /*
     * Fetches all businesses when the component mounts.
     * * The list of businesses is used to form the drop-down options that allow the user to select an existing business.
     * * The list is also used to search for the currently selected business at page load time, if a business was selected/created before the page was reloaded.
     */
    useEffect(() => {
        // Prevent prematurely searching data or re-fetching initial data.
        if (businessSelectionOrCreationState !== undefined) {
            return;
        }

        const getBusinesses = async () => {
            try {
                let businesses: BusinessResponseWithUseCases[] | BusinessWithUseCasesForNonUcrmUser[];

                if (userHasFullUcrmAccess) {
                    businesses = (await props.ucrmApi.getBusinesses()).data;
                    setBusinesses(businesses);
                } else {
                    businesses = (await props.ucrmApi.getBusinessesForNonUcrmUser()).data;
                    setBusinesses(businesses);
                }

                if (urlState.organizationId) {
                    const business = businesses.find((b) => b.id === urlState.organizationId)!;
                    setBusinessSelectionOrCreationState({ type: 'savedOrganizationSelected', organization: business });
                } else {
                    setBusinessSelectionOrCreationState({ type: 'notStarted' });
                }
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getBusinesses();
    }, [props.ucrmApi, urlState.organizationId, businessSelectionOrCreationState, userHasFullUcrmAccess]);

    /*
     * Fetches due diligence assessments configuration when the component mounts, if the user has full access to UCRM.
     */
    useEffect(() => {
        const getAssessmentsConfiguration = async () => {
            try {
                const configurations = (await props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()).data;
                setAssessmentsConfiguration(configurations);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (userHasFullUcrmAccess) {
            getAssessmentsConfiguration();
        } else {
            setAssessmentsConfiguration(null);
        }
    }, [props.dueDiligenceAssessmentConfigurationApi, props.ucrmApi, userHasFullUcrmAccess]);

    /*
     * Fetches TPRM third-party services when the component mounts, if the user can map use cases to them. Failing to get them does not prevent the user from completing the intake.
     */
    useEffect(() => {
        const getTprmServices = async () => {
            try {
                setTprmServices((await props.tprmModuleApi.getThirdPartyServices()).data);
            } catch (error) {
                setTprmServices(null);
                setToastContent({ variant: 'failure', text: error.message });
            }
        };

        if (userCanMapTprmServices) {
            getTprmServices();
        } else {
            setTprmServices(null);
        }
    }, [props.tprmModuleApi, userCanMapTprmServices]);

    useEffect(() => {
        const getTimeframes = async () => {
            try {
                const response = await props.dueDiligenceAssessmentConfigurationApi.getTimeframes();
                setTimeframes(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        if (userHasFullUcrmAccess) {
            getTimeframes();
        } else {
            setTimeframes(null);
        }
    }, [props.dueDiligenceAssessmentConfigurationApi, userHasFullUcrmAccess]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (businesses === undefined || businessSelectionOrCreationState === undefined || useCase === undefined || irqAndValidation === undefined || intakeInstructions === undefined || timeframes === undefined || tprmServices === undefined) {
        return <Placeholder />;
    }

    const irqTargetCompletionInDays = timeframes?.expected_days_to_complete_irq;
    const irqTargetCompletionDateArgs = { action: 'startServiceAssessmentWorkflow' as const, currentAssessmentWorkflowIrqTargetCompletionDate: undefined, expectedIrqDurationInDays: irqTargetCompletionInDays };
    const recommendedIrqTargetCompletionDate = getDefaultIrqTargetCompletionDate(irqTargetCompletionDateArgs);
    const irqWillNeedToBeCompleted = !irqCheckboxChecked;
    const recommendedDueDiligenceWorkflowDueDate = assessmentConfigurations && timeframes ? getRecommendedDueDiligenceWorkflowDueDate(irqWillNeedToBeCompleted ? recommendedIrqTargetCompletionDate : undefined, assessmentConfigurations, timeframes) : undefined;

    // For each date, the user's explicit selection takes precedence; otherwise the recommended date is used (and submitted) by default.
    const irqTargetCompletionDate = irqTargetCompletionDateOverride ? irqTargetCompletionDateOverride.date : recommendedIrqTargetCompletionDate;
    const dueDiligenceWorkflowDueDate = dueDiligenceWorkflowDueDateOverride ? dueDiligenceWorkflowDueDateOverride.date : recommendedDueDiligenceWorkflowDueDate;

    const businessOptions: OptionType[] = businesses.map((business) => ({ value: business.id, label: business.name }));
    const business = businessSelectionOrCreationState.type === 'savedOrganizationSelected' ? businessSelectionOrCreationState.organization : undefined;
    const userCanSubmitBusiness = !!business && userCanSubmitOrFinalizeDraft(userId, userHasFullUcrmAccess, business);
    const userCanSubmitUseCase = !!useCase && userCanSubmitOrFinalizeDraft(userId, userHasFullUcrmAccess, useCase);

    const onRequestFailed = (errorMessage: string) => {
        setToastContent({ variant: 'failure', text: errorMessage });
    };

    const onBusinessSaved = async (savedBusiness: BusinessResponse | BusinessForNonUcrmUser) => {
        setUrlState((current) => ({
            ...current,
            step: current.step === IntakeWorkflowStep.ORGANIZATION ? IntakeWorkflowStep.ASSESSMENT_SUBJECT : current.step,
            organizationId: savedBusiness.id,
            organizationActions: {
                ...current.organizationActions,
                created: businessSelectionOrCreationState.type !== 'savedOrganizationSelected' ? true : current.organizationActions.created,
                saved: true,
            },
        }));

        // TODO: It's quite hacky to hardcode `use_cases` and use `as` below, even though we know:
        // - the operation of saving a business can't change the list of use cases associated with the business being created/updated in the first step of the page's workflow.
        // - creating a new use case in the second step of the page's workflow won't/shouldn't impact the use cases listed for a business that existed before this page was visited, since the point is to list preexisting use cases so the user doesn't create a duplicate. (A use case created on this page is filtered out in the JSX below regardless.)
        // This hack is necessary because of the overly numerous interfaces for businesses that we have defined; the backend is not returning objects with a `use_cases` property for the endpoints used to create/update a business, and it doesn't seem straightforward to change this.
        setBusinessSelectionOrCreationState((currentState) => {
            const currentBusiness = currentState?.type === 'savedOrganizationSelected' ? currentState.organization : undefined;
            const currentUseCases = currentBusiness?.use_cases ?? [];

            if (userHasFullUcrmAccess) {
                return { type: 'savedOrganizationSelected', organization: { ...savedBusiness, use_cases: currentUseCases as UseCase[] } as BusinessResponseWithUseCases };
            } else {
                return { type: 'savedOrganizationSelected', organization: { ...savedBusiness, use_cases: currentUseCases as UseCaseForNonUcrmUser[] } as BusinessWithUseCasesForNonUcrmUser };
            }
        });

        setToastContent({ variant: 'success', text: UCRM_SUCCESS_MESSAGE_BUSINESS });
    };

    const onUseCaseSaved = async (savedUseCase: AssessmentSubjectType<UcrmEntities>) => {
        try {
            if (!irqAndValidation.irq) {
                const irq = (await props.ucrmApi.getInherentRiskQuestionnaire(savedUseCase.vendor_id, savedUseCase.id)).data!;
                updateIrqAndSubmitIrqValidation(irq);
            }
            setUrlState((current) => ({
                ...current,
                step: current.step === IntakeWorkflowStep.ASSESSMENT_SUBJECT ? IntakeWorkflowStep.IRQ : current.step,
                assessmentSubjectId: savedUseCase.id,
                assessmentSubjectActions: {
                    ...current.assessmentSubjectActions,
                    created: !useCase ? true : current.assessmentSubjectActions.created,
                    saved: true,
                },
            }));
            setUseCase(savedUseCase);
            setToastContent({ variant: 'success', text: UCRM_SUCCESS_MESSAGE_USE_CASE });
        } catch (error) {
            onRequestFailed(error.message);
        }
    };

    const onIrqSaved = async (updatedIrq: InherentRiskQuestionnaireResponse) => {
        updateIrqAndSubmitIrqValidation(updatedIrq, userHasFullUcrmAccess);
        setUrlState((current) => ({
            ...current,
            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
            irqActions: { ...current.irqActions, saved: true },
        }));
        setToastContent({ variant: 'success', text: SUCCESS_MESSAGE_IRQ });
    };

    const expandedSteps: IntakeWorkflowStep[] = (() => {
        const _expandedStepsBeforeSkipsAccountedFor: IntakeWorkflowStep[] = (() => {
            switch (urlState.step) {
                case IntakeWorkflowStep.ORGANIZATION:
                    return [IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.ASSESSMENT_SUBJECT:
                    return [IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.IRQ:
                    return [IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.CHOOSE_SUBMISSIONS:
                    return [IntakeWorkflowStep.CHOOSE_SUBMISSIONS, IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
                case IntakeWorkflowStep.COMPLETE:
                    return [IntakeWorkflowStep.CHOOSE_SUBMISSIONS, IntakeWorkflowStep.IRQ, IntakeWorkflowStep.ASSESSMENT_SUBJECT, IntakeWorkflowStep.ORGANIZATION];
            }
        })();

        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            return _expandedStepsBeforeSkipsAccountedFor;
        }

        const _expandedSteps = _expandedStepsBeforeSkipsAccountedFor.filter((step) => {
            if (urlState.assessmentSubjectActions.skipped) {
                return step !== IntakeWorkflowStep.ASSESSMENT_SUBJECT;
            }
            if (urlState.irqActions.skipped) {
                return step !== IntakeWorkflowStep.IRQ;
            }
            return true;
        });
        return _expandedSteps;
    })();

    /**
     * Either the business form or a fragment. `key` _must_ be used for the form component, as the "purpose" of the form will change (create-->edit) once the business is saved, and the form component expects to be rendered freshly for a given purpose.
     * A fragment is returned when the user does not have full access to UCRM, and has selected a business that they are not allowed to edit (i.e., one that is finalized or that they did not create). In this case, the business's name will still be displayed in the selection drop-down, but the form for editing it will not be rendered.
     * Once the workflow is complete, a summary of what was done to the business is returned instead.
     */
    const businessForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const name = business!.name;
            const created = urlState.organizationActions.created;
            const edited = urlState.organizationActions.saved;

            let summaryString = '';
            if (created) {
                summaryString = `${name} was created.`;
            } else {
                summaryString = edited ? `${name} was selected and changes were saved.` : `${name} was selected.`;
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        // If the business has been created and saved, or has been loaded as initial data for the page, then the user can only change the business's name, in which case this element is not used and the business name is managed by the child.
        // If a saved business is not present, then the user can edit the name or select a different business, in which case this element is used and the business name is managed by this component.
        const businessNameField = (
            <div className={styles.formFieldContainer}>
                <FormFieldSelectOrCreate
                    required
                    tooltip={UCRM_BUSINESS_NAME_SELECT_OR_CREATE}
                    formFieldId="selectOrCreateBusiness"
                    formFieldLabel="Legal Name"
                    handleChange={(value, isCreateNew) => {
                        if (isCreateNew) {
                            setBusinessSelectionOrCreationState({ type: 'newOrganizationNameEntered', name: value as string });
                        } else {
                            const business = businesses.find((b) => b.id === value);
                            if (business) {
                                setBusinessSelectionOrCreationState({ type: 'savedOrganizationSelected', organization: business });
                                setUrlState((current) => ({
                                    ...current,
                                    step: IntakeWorkflowStep.ASSESSMENT_SUBJECT,
                                    organizationId: business.id,
                                }));
                            } else {
                                setBusinessSelectionOrCreationState({ type: 'notStarted' });
                            }
                        }
                    }}
                    options={businessOptions}
                />
            </div>
        );

        switch (businessSelectionOrCreationState?.type) {
            case 'savedOrganizationSelected':
                const userCanEditBusiness = userHasFullAccessOrCreatedDraft(userId, userHasFullUcrmAccess, businessSelectionOrCreationState.organization);
                if (userCanEditBusiness) {
                    const userCanSelectDifferentBusiness = !useCase && !urlState.completingIntake && !urlState.organizationActions.created;
                    return (
                        <div data-testid={ORGANIZATION_FORM_TEST_ID}>
                            {userCanSelectDifferentBusiness && businessNameField}
                            <ManageBusinessFormFields key="manageCreatedBusiness" hideDeleteButton issuesApi={props.issuesApi} exceptionsApi={props.exceptionsApi} business={businessSelectionOrCreationState.organization} userHasFullUcrmAccess={userHasFullUcrmAccess} ucrmApi={props.ucrmApi} name={userCanSelectDifferentBusiness ? businessSelectionOrCreationState.organization.name : undefined} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onBusinessSaved} onFormSubmitFailed={onRequestFailed} />
                        </div>
                    );
                } else {
                    return <></>;
                }
            case 'newOrganizationNameEntered':
                return (
                    <div data-testid={ORGANIZATION_FORM_TEST_ID}>
                        {businessNameField}
                        <ManageBusinessFormFields key="createNewBusiness" hideDeleteButton issuesApi={props.issuesApi} exceptionsApi={props.exceptionsApi} userHasFullUcrmAccess={userHasFullUcrmAccess} ucrmApi={props.ucrmApi} name={businessSelectionOrCreationState.name} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onBusinessSaved} onFormSubmitFailed={onRequestFailed} />
                    </div>
                );
            case 'notStarted':
                return <div data-testid={ORGANIZATION_FORM_TEST_ID}>{businessNameField}</div>;
            case undefined:
                return <></>;
        }
    })();

    /**
     * Either the use case form (preceded by a list of the business's other use cases, if any) or a fragment if a saved business is not yet selected. `key` _must_ be used for the form component, as the "purpose" of the form will change (create-->edit) once the use case is saved, and the form component expects to be rendered freshly for a given purpose.
     * Once the workflow is complete, a summary of what was done to the use case is returned instead.
     */
    const useCaseForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const skipped = urlState.assessmentSubjectActions.skipped;
            const created = urlState.assessmentSubjectActions.created;
            const edited = urlState.assessmentSubjectActions.saved;

            let summaryString = '';
            if (skipped) {
                summaryString = 'This step was skipped.';
            } else if (created) {
                summaryString = `${useCase!.name} was created.`;
            } else {
                summaryString = edited ? `${useCase!.name} was selected and changes were saved.` : `${useCase!.name} was selected.`;
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        const otherUseCases = businessSelectionOrCreationState.type === 'savedOrganizationSelected' ? businessSelectionOrCreationState.organization.use_cases.filter((otherUseCase) => otherUseCase.id !== useCase?.id) : [];
        const existingUseCasesList =
            businessSelectionOrCreationState.type === 'savedOrganizationSelected' && otherUseCases.length > 0 ? (
                <>
                    <div className={styles.existingUseCases}>
                        <Text variant="Header3">Existing Use Cases</Text>
                        <ul className={styles.list}>
                            {[...otherUseCases]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((existingUseCase) => (
                                    <li key={existingUseCase.id}>
                                        <Text noStyles>{existingUseCase.name}</Text>
                                    </li>
                                ))}
                        </ul>
                    </div>
                    <Text variant="Header3">New Use Case</Text>
                </>
            ) : (
                <></>
            );

        if (businessSelectionOrCreationState.type !== 'savedOrganizationSelected') {
            return <></>;
        } else if (!!useCase) {
            return (
                <div data-testid={ASSESSMENT_SUBJECT_FORM_TEST_ID}>
                    {existingUseCasesList}
                    <ManageUseCaseFormFields key="manageCreatedUseCase" artificialIntelligenceApi={props.artificialIntelligenceApi} useCase={useCase} hideDeleteButton userHasFullUcrmAccess={userHasFullUcrmAccess} ucrmApi={props.ucrmApi} business={businessSelectionOrCreationState.organization} tprmServices={tprmServices ?? undefined} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onUseCaseSaved} onFormSubmitFailed={onRequestFailed} />
                </div>
            );
        } else {
            return (
                <div data-testid={ASSESSMENT_SUBJECT_FORM_TEST_ID}>
                    {existingUseCasesList}
                    <ManageUseCaseFormFields key="createNewUseCase" artificialIntelligenceApi={props.artificialIntelligenceApi} hideDeleteButton userHasFullUcrmAccess={userHasFullUcrmAccess} ucrmApi={props.ucrmApi} business={businessSelectionOrCreationState.organization} tprmServices={tprmServices ?? undefined} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmittedSuccessfully={onUseCaseSaved} onFormSubmitFailed={onRequestFailed} />
                </div>
            );
        }
    })();

    /**
     * Either the IRQ form for the saved use case or a fragment if a saved business, a saved use case, or the use case's IRQ is not yet available.
     * Once the workflow is complete, a summary of what was done to the IRQ is returned instead.
     */
    const irqForm: JSX.Element = (() => {
        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const skipped = urlState.irqActions.skipped;
            const edited = urlState.irqActions.saved;

            let summaryString = '';
            if (skipped) {
                summaryString = 'This step was skipped.';
            } else {
                summaryString = edited ? 'Changes were saved.' : 'No changes were saved.';
            }

            return <Text noStyles>{summaryString}</Text>;
        }

        const irq = irqAndValidation.irq;
        if (businessSelectionOrCreationState.type === 'savedOrganizationSelected' && useCase && irq) {
            const userTypeAndData: InherentRiskQuestionnaireUserTypeAndData = userHasFullUcrmAccess ? { userHasFullAccess: true, historical: false, assessmentConfigurations: assessmentConfigurations!, defaultIrq: irq, assessmentSubject: useCase as UseCase } : { userHasFullAccess: false, historical: false, defaultIrq: irq, assessmentSubject: useCase as UseCaseForNonUcrmUser };
            return (
                <div data-testid={IRQ_FORM_TEST_ID}>
                    <InherentRiskQuestionnaireForm artificialIntelligenceApi={props.artificialIntelligenceApi} documentApi={props.documentApi} highlightMissingInformation={irqAndValidation.highlightMissingInformation} module={ProductModule.UCRM} onFormSubmitBegin={() => setToastContent(undefined)} onFormSubmitSucceeded={onIrqSaved} onFormSubmitFailed={onRequestFailed} moduleApi={props.ucrmApi} userTypeAndData={userTypeAndData} />
                </div>
            );
        } else {
            return <></>;
        }
    })();

    // The checkboxes are checked by default and remain checked (but disabled) for items that cannot be submitted, such as an item that a user without full access already submitted to be finalized, so whether the user can submit each item must also be checked here.
    const businessWillBeSubmitted = userCanSubmitBusiness && businessCheckboxChecked;
    const useCaseWillBeSubmitted = userCanSubmitUseCase && useCaseCheckboxChecked;
    const irqWillBeSubmitted = irqCheckboxChecked;

    const chooseSubmissionsForm: JSX.Element = (() => {
        if (!business) {
            return <></>;
        }

        if (urlState.step === IntakeWorkflowStep.COMPLETE) {
            const useCaseWasSkipped = urlState.assessmentSubjectActions.skipped;
            const irqWasSkipped = urlState.irqActions.skipped;
            const skippedText = (() => {
                if (useCaseWasSkipped) {
                    return 'Because the use case and inherent risk questionnaire were skipped,';
                } else if (irqWasSkipped) {
                    return 'Because the inherent risk questionnaire was skipped,';
                } else {
                    return undefined;
                }
            })();

            return (
                <>
                    {urlState.organizationActions.submitted && <Text>{`${business.name} was ${userHasFullUcrmAccess ? 'finalized.' : 'submitted to be finalized.'}${userHasFullUcrmAccess ? '' : ` ${getUserNameFromSubject(business.vendor_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} has been notified.`}`}</Text>}
                    {urlState.assessmentSubjectActions.submitted && useCase && <Text>{`${useCase.name} was ${userHasFullUcrmAccess ? 'finalized.' : 'submitted to be finalized.'}${userHasFullUcrmAccess ? ' The due diligence workflow has been started.' : ` ${getUserNameFromSubject(useCase.vendor_service_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} has been notified.`}`}</Text>}
                    {userHasFullUcrmAccess && urlState.irqActions.submitted && <Text>The inherent risk questionnaire was submitted. The due diligence questionnaires, due diligence assessments, and final review are available for changes.</Text>}
                    {userHasFullUcrmAccess && !urlState.irqActions.submitted && skippedText === undefined && <Text>The inherent risk questionnaire was not submitted. The due diligence questionnaires, due diligence assessments, and final review are not available for changes.</Text>}
                    {userHasFullUcrmAccess && !urlState.irqActions.submitted && skippedText !== undefined && <Text>{`${skippedText} the due diligence questionnaires, assessments, and final review are not available for changes.`}</Text>}
                </>
            );
        }

        const submitIrqValidationErrorMessage = irqAndValidation.submitIrqValidationErrorMessage;

        const businessTooltip: string = (() => {
            if (userHasFullUcrmAccess) {
                return business.finalization_state === FinalizationState.FINALIZED ? UCRM_INTAKE_BUSINESS_ALREADY_FINALIZED : UCRM_INTAKE_SUBMIT_BUSINESS_CHECKBOX_UCRM_USER;
            } else {
                switch (business.finalization_state) {
                    case FinalizationState.DRAFT_IN_PROGRESS:
                        return userCanSubmitBusiness ? UCRM_INTAKE_SUBMIT_BUSINESS_CHECKBOX_NON_UCRM_USER : UCRM_INTAKE_BUSINESS_CANNOT_BE_SUBMITTED_BECAUSE_NON_UCRM_USER_IS_NOT_CREATOR;
                    case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                        return UCRM_INTAKE_BUSINESS_ALREADY_SUBMITTED_TO_BE_FINALIZED;
                    case FinalizationState.FINALIZED:
                        return UCRM_INTAKE_BUSINESS_ALREADY_FINALIZED;
                }
            }
        })();

        const useCaseTooltip: string = (() => {
            if (useCase === null) {
                return UCRM_INTAKE_USE_CASE_CANNOT_BE_SUBMITTED_BECAUSE_USE_CASE_NOT_CREATED;
            } else if (userHasFullUcrmAccess) {
                return useCase.finalization_state === FinalizationState.FINALIZED ? UCRM_INTAKE_USE_CASE_ALREADY_FINALIZED : UCRM_INTAKE_SUBMIT_USE_CASE_CHECKBOX_UCRM_USER;
            } else {
                switch (useCase.finalization_state) {
                    case FinalizationState.DRAFT_IN_PROGRESS:
                        return UCRM_INTAKE_SUBMIT_USE_CASE_CHECKBOX_NON_UCRM_USER;
                    case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                        return UCRM_INTAKE_USE_CASE_ALREADY_SUBMITTED_TO_BE_FINALIZED;
                    case FinalizationState.FINALIZED:
                        return UCRM_INTAKE_USE_CASE_ALREADY_FINALIZED;
                }
            }
        })();

        const irqTooltip: string = (() => {
            if (urlState.assessmentSubjectActions.skipped) {
                return urlState.irqActions.skipped ? UCRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED : UCRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_USE_CASE_STEP_SKIPPED;
            } else if (urlState.irqActions.skipped) {
                return UCRM_INTAKE_IRQ_CANNOT_BE_SUBMITTED_BECAUSE_IRQ_STEP_SKIPPED;
            } else {
                return `${UCRM_INTAKE_SUBMIT_IRQ_CHECKBOX_UCRM_USER}${submitIrqValidationErrorMessage ? ` ${submitIrqValidationErrorMessage}` : ''}`;
            }
        })();

        return (
            <div data-testid={SUBMIT_FORM_TEST_ID}>
                <Checkbox
                    checked={businessCheckboxChecked}
                    disabled={!userCanSubmitBusiness}
                    label={userHasFullUcrmAccess ? 'Finalize business' : 'Submit business to be finalized'}
                    onToggled={(isChecked) => {
                        setBusinessCheckboxChecked(isChecked);
                        if (!isChecked) {
                            setUseCaseCheckboxChecked(false);
                            setIrqCheckboxChecked(false);
                            setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                        }
                    }}
                    tooltip={businessTooltip}
                />
                <Checkbox
                    checked={useCaseCheckboxChecked}
                    disabled={!userCanSubmitUseCase}
                    label={userHasFullUcrmAccess ? 'Finalize use case' : 'Submit use case to be finalized'}
                    onToggled={(isChecked) => {
                        setUseCaseCheckboxChecked(isChecked);
                        if (isChecked) {
                            setBusinessCheckboxChecked(true);
                        } else {
                            setIrqCheckboxChecked(false);
                            setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                        }
                    }}
                    tooltip={useCaseTooltip}
                />
                {userHasFullUcrmAccess && (
                    <Checkbox
                        checked={irqCheckboxChecked}
                        disabled={urlState.irqActions.skipped}
                        label="Submit inherent risk questionnaire"
                        onToggled={(isChecked) => {
                            setIrqCheckboxChecked(isChecked);
                            if (isChecked) {
                                setBusinessCheckboxChecked(true);
                                setUseCaseCheckboxChecked(true);
                            } else {
                                setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: false }));
                            }
                        }}
                        tooltip={irqTooltip}
                    />
                )}
                <div className={styles.submitSelectionsContainer}>
                    {useCaseWillBeSubmitted && !irqWillBeSubmitted && userHasFullUcrmAccess && <IrqTargetCompletionDateSelection {...getIrqTargetCompletionDateSelectionProps(ProductModule.UCRM, irqTargetCompletionDateArgs, (date: Date | undefined) => setIrqTargetCompletionDateOverride({ date }))} />}
                    {useCaseWillBeSubmitted && userHasFullUcrmAccess && (
                        <div>
                            <FormFieldDatePicker dateFormat="MM/dd/yyyy" selected={dueDiligenceWorkflowDueDate} handleChange={(date: Date | undefined | null) => setDueDiligenceWorkflowDueDateOverride({ date: date ?? undefined })} formFieldId="dueDiligenceWorkflowDueDate" formFieldLabel="Due Diligence Workflow Due Date" placeholder="MM/DD/YYYY" tooltip={UCRM_DUE_DILIGENCE_WORKFLOW_DUE_DATE_WITH_RECOMMENDATION} />
                            {recommendedDueDiligenceWorkflowDueDate && <Text color="darkGray">Recommended: {moment(recommendedDueDiligenceWorkflowDueDate).format('MM/DD/YYYY')}</Text>}
                        </div>
                    )}
                    {irqWillBeSubmitted && useCase && assessmentConfigurations && userHasFullUcrmAccess && submitIrqValidationErrorMessage === undefined && (
                        <div className={styles.submitIrqSelections}>
                            <SubmitIrqSelections assessments={assessmentConfigurations} defaultQuestionnaireRespondentIds={[useCase.vendor_service_manager_user_id, ...useCase.delegates]} inherentRiskRating={irqAndValidation.irq?.inherent_risk_score ?? RiskRating.UNRATED} module={ProductModule.UCRM} onSelectionsChanged={onSubmitIrqSelectionsChanged} />
                        </div>
                    )}
                </div>
                <div className={styles.completeButtonContainer}>
                    <Button
                        variant="primary"
                        onClick={() => {
                            // The IRQ is treated uniquely because it tends to be lengthy and difficult to scan visually for missing information. The IRQ checkbox can be checked even if the user can't actually submit the IRQ. If the user tries to submit the IRQ without completing it, highlight missing information for the user.
                            if (irqWillBeSubmitted && submitIrqValidationErrorMessage) {
                                setToastContent({ variant: 'failure', text: submitIrqValidationErrorMessage });
                                setIrqAndValidation((current) => ({ ...current, highlightMissingInformation: true }));
                            } else {
                                setToastContent(undefined);
                                setDisplayConfirmationModal(true);
                            }
                        }}
                        isLoading={false}
                        loadingText="Completing..."
                        fontAwesomeImage={ICON_SUBMIT}
                    >
                        Submit
                    </Button>
                </div>
            </div>
        );
    })();

    /** The modal that confirms the user's submissions when they click Submit in the final step; on success, the workflow moves to the Complete step. A fragment is returned when the modal is hidden, or when a business has not been selected. */
    const confirmationModal = (() => {
        if (!displayConfirmationModal || business === undefined || useCase === undefined) {
            return <></>;
        }

        return (
            <ConfirmCompleteFormModal
                // Commenting to force newlines.
                business={business}
                businessWillBeSubmitted={businessWillBeSubmitted}
                dueDiligenceWorkflowDueDate={dueDiligenceWorkflowDueDate}
                hideModal={() => setDisplayConfirmationModal(false)}
                irqTargetCompletionDate={irqTargetCompletionDate}
                irqWillBeSubmitted={irqWillBeSubmitted}
                onSuccess={() =>
                    setUrlState((current) => ({
                        ...current,
                        step: IntakeWorkflowStep.COMPLETE,
                        organizationActions: { ...current.organizationActions, submitted: businessWillBeSubmitted },
                        assessmentSubjectActions: { ...current.assessmentSubjectActions, submitted: useCaseWillBeSubmitted },
                        irqActions: { ...current.irqActions, submitted: irqWillBeSubmitted },
                    }))
                }
                submitIrqSelections={submitIrqSelections}
                ucrmApi={props.ucrmApi}
                useCase={useCase}
                useCaseWillBeSubmitted={useCaseWillBeSubmitted}
                userHasFullUcrmAccess={userHasFullUcrmAccess}
            />
        );
    })();

    // If a vertical stepper is ever needed elsewhere in the app, consider extracting the structure of this stepper into a reusable component, replacing/augmenting the existing `Stepper` to support horizontal and vertical steppers. The existing `Stepper` doesn't really do anything except standardize some styling.
    return (
        <>
            {toastContent && <TextToast clearToast={() => setToastContent(undefined)} {...toastContent} />}
            {confirmationModal}

            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING)}>Businesses and Use Cases</BreadcrumbLink>
                        <BreadcrumbText>Create</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Create"
                headerDescription={intakeInstructions}
                body={[
                    {
                        content: (
                            <Stepper
                                activeStep={urlState.step}
                                orientation="vertical"
                                sx={{
                                    '& .MuiStepIcon-root': {
                                        // All of these colors are dark blue because the user can move freely between steps (once they are "unlocked" by creating prerequisite items), and the step indicators are never changed from step numbers to checkmarks.
                                        color: 'var(--hps-dark-blue)', // The color of the "step" circle before the step is active or completed.
                                        '&.Mui-active': {
                                            color: 'var(--hps-dark-blue)', // The color of the "step" circle when the step is active (this corresponds to `activeStep` above).
                                        },
                                        '&.Mui-completed': {
                                            color: 'var(--hps-dark-blue)', // The color of the "step" circle when the step is completed.
                                        },
                                    },
                                    '& .MuiStepContent-root': {
                                        borderColor: 'var(--hps-gray)', // The color of the left border of the step content area, which functions as part of the connecting line between steps.
                                    },
                                    '& .MuiStepConnector-line': {
                                        borderColor: 'var(--hps-gray)', // The color of the left border of the vertical space between steps, which functions as part of the connecting line between steps.
                                    },
                                }}
                            >
                                {/* Note: All steps are hardcoded as not completed so that the step indicators are never changed from step numbers to checkmarks. (The user can move freely between the steps; checkmarks may be confusing.) */}
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.ORGANIZATION)} completed={false}>
                                    <StepLabel>
                                        <Text noStyles variant="Header2">
                                            Business
                                        </Text>
                                    </StepLabel>
                                    <StepContent>{businessForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.ASSESSMENT_SUBJECT)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Use Case
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.ASSESSMENT_SUBJECT && userCanSubmitBusiness && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
                                                            assessmentSubjectActions: {
                                                                ...current.assessmentSubjectActions,
                                                                skipped: true,
                                                            },
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: true,
                                                            },
                                                        }));
                                                        setUseCaseCheckboxChecked(false);
                                                        setIrqCheckboxChecked(false);
                                                    }}
                                                >
                                                    Skip this step
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{useCaseForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.IRQ)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Inherent Risk Questionnaire
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.IRQ && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: IntakeWorkflowStep.CHOOSE_SUBMISSIONS,
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: true,
                                                            },
                                                        }));
                                                        setIrqCheckboxChecked(false);
                                                    }}
                                                >
                                                    Skip this step
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{irqForm}</StepContent>
                                </Step>
                                <Step expanded={expandedSteps.includes(IntakeWorkflowStep.CHOOSE_SUBMISSIONS)} completed={false}>
                                    <StepLabel>
                                        <div className={styles.stepLabelContainer}>
                                            <Text noStyles variant="Header2">
                                                Submit
                                            </Text>
                                            {urlState.step === IntakeWorkflowStep.CHOOSE_SUBMISSIONS && (urlState.assessmentSubjectActions.skipped || urlState.irqActions.skipped) && (
                                                <Button
                                                    variant="linkText"
                                                    size="sm"
                                                    onClick={() => {
                                                        setUrlState((current) => ({
                                                            ...current,
                                                            step: current.assessmentSubjectActions.skipped ? IntakeWorkflowStep.ASSESSMENT_SUBJECT : IntakeWorkflowStep.IRQ,
                                                            assessmentSubjectActions: {
                                                                ...current.assessmentSubjectActions,
                                                                skipped: false,
                                                            },
                                                            irqActions: {
                                                                ...current.irqActions,
                                                                skipped: false,
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    Return to skipped steps
                                                </Button>
                                            )}
                                        </div>
                                    </StepLabel>
                                    <StepContent>{chooseSubmissionsForm}</StepContent>
                                </Step>
                            </Stepper>
                        ),
                    },
                ]}
            />
        </>
    );
};

/**
 * @param useCase The use case created/selected in the intake workflow, or `null` if no use case was created/selected (i.e., the use case step was skipped).
 */
interface ConfirmCompleteFormModalProps {
    business: OrganizationType<UcrmEntities>;
    businessWillBeSubmitted: boolean;
    hideModal: () => void;
    irqWillBeSubmitted: boolean;
    onSuccess: () => void;
    submitIrqSelections: UcrmSubmitInherentRiskQuestionnaireSelections;
    ucrmApi: ClientUcrmApi;
    useCase: AssessmentSubjectType<UcrmEntities> | null;
    useCaseWillBeSubmitted: boolean;
    userHasFullUcrmAccess: boolean;

    dueDiligenceWorkflowDueDate?: Date;
    irqTargetCompletionDate?: Date;
}

/**
 * The confirmation modal for the final step of `UcrmIntake`, which completes the intake by submitting whichever of the business, use case, and IRQ the user selected (submitting nothing leaves new items as drafts).
 * For a UCRM user, submitting finalizes the business/use case (and starts the use case's due diligence workflow); for other users, it submits them to be finalized by their managers, who are notified.
 * On success, displays a link to the use case's dashboard if a UCRM user finalized the use case, or back to the Businesses and Use Cases listing otherwise.
 */
const ConfirmCompleteFormModal = (props: ConfirmCompleteFormModalProps) => {
    const { users } = useCachedData();

    const atLeastOneItemWillBeSubmitted = props.businessWillBeSubmitted || props.useCaseWillBeSubmitted || props.irqWillBeSubmitted;

    return (
        <ConfirmationModal
            operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
            headerText="Submit Form"
            buttonText="Submit"
            buttonIcon={ICON_SUBMIT}
            buttonLoadingText="Submitting..."
            areYouSureText={atLeastOneItemWillBeSubmitted ? 'Are you sure you want to submit the selected items?' : 'Are you sure you want to submit the form?'}
            hideModal={props.hideModal}
            performOperation={async () => {
                const businessId = props.business.id;
                const useCaseId = props.useCase?.id;

                const request: CompleteUcrmIntakeRequest = {
                    submit_organization: props.businessWillBeSubmitted,
                    submit_assessment_subject: props.useCaseWillBeSubmitted,
                    submit_irq: props.irqWillBeSubmitted,
                    submit_irq_selections: props.submitIrqSelections,
                    organization_id: businessId,
                    assessment_subject_id: useCaseId,
                    due_diligence_workflow_due_date: props.dueDiligenceWorkflowDueDate ? jsDateToIso8601(props.dueDiligenceWorkflowDueDate) : undefined,
                    irq_target_completion_date: props.irqTargetCompletionDate ? jsDateToIso8601(props.irqTargetCompletionDate) : undefined,
                };

                await props.ucrmApi.completeUcrmIntake(request);
                props.onSuccess();

                const finalizedUseCaseId: string | undefined = props.useCase && props.useCaseWillBeSubmitted ? props.useCase.id : undefined;

                return (
                    <>
                        <Text>Form submitted.</Text>
                        <Link size="sm" to={props.userHasFullUcrmAccess && finalizedUseCaseId ? generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: businessId, service_id: finalizedUseCaseId }) : generatePath(UCRM_BUSINESSES_AND_USE_CASES_LISTING, {})}>
                            {props.userHasFullUcrmAccess && finalizedUseCaseId ? 'View Use Case Dashboard' : 'Return to Businesses and Use Cases'}
                        </Link>
                    </>
                );
            }}
        >
            {!atLeastOneItemWillBeSubmitted && <Text>Because new items were not selected to be submitted, new items will remain as drafts.</Text>}
            {props.businessWillBeSubmitted && <Text>{props.userHasFullUcrmAccess ? 'The business will no longer be a draft.' : `The business will no longer be a draft, and ${getUserNameFromSubject(props.business.vendor_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} will be notified.`}</Text>}
            {props.useCaseWillBeSubmitted && props.useCase && <Text>{props.userHasFullUcrmAccess ? 'The use case will no longer be a draft, and the due diligence workflow will be started.' : `The use case will no longer be a draft, and ${getUserNameFromSubject(props.useCase.vendor_service_manager_user_id, users, UserNameFormat.FIRST_SPACE_LAST)} will be notified.`}</Text>}
            {props.irqWillBeSubmitted && <Text>Submitting the inherent risk questionnaire will make the due diligence questionnaires, due diligence assessments, and final review available for changes.</Text>}
        </ConfirmationModal>
    );
};
